import React, { memo, useMemo } from 'react'
import { Box, useTheme } from '@mui/material'

const BAR_HEIGHTS = [
    [52, 38],
    [68, 45],
    [40, 62],
    [75, 50],
    [48, 70],
    [82, 55],
    [44, 36],
    [60, 78],
    [55, 42],
    [70, 58],
    [38, 65],
    [64, 48],
]

/** OI widget skeleton: grouped vertical bars per strike, not a line chart. */
const OiChartSkeletonPlaceholder = () => {
    const theme = useTheme()
    const isDark = theme.palette.mode === 'dark'

    const colors = useMemo(
        () => ({
            fill: isDark ? 'rgba(255, 255, 255, 0.12)' : 'rgba(0, 0, 0, 0.09)',
            muted: isDark ? 'rgba(255, 255, 255, 0.07)' : 'rgba(0, 0, 0, 0.06)',
            scale: isDark ? 'rgba(255, 255, 255, 0.08)' : 'rgba(0, 0, 0, 0.06)',
            grid: isDark ? 'rgba(255, 255, 255, 0.05)' : 'rgba(0, 0, 0, 0.04)',
            axis: isDark ? 'rgba(255, 255, 255, 0.12)' : 'rgba(0, 0, 0, 0.1)',
        }),
        [isDark]
    )

    return (
        <Box
            sx={{
                width: '100%',
                height: '100%',
                minHeight: 0,
                bgcolor: 'background.paper',
                display: 'flex',
                flexDirection: 'column',
                pointerEvents: 'none',
                overflow: 'hidden',
            }}
        >
            <Box
                sx={{
                    flex: 1,
                    minHeight: 0,
                    display: 'flex',
                    position: 'relative',
                    backgroundImage: `linear-gradient(${colors.grid} 1px, transparent 1px)`,
                    backgroundSize: '100% 28px',
                }}
            >
                <Box
                    sx={{
                        flex: 1,
                        minWidth: 0,
                        display: 'flex',
                        alignItems: 'flex-end',
                        gap: '3px',
                        px: 0.5,
                        pb: 0.5,
                    }}
                >
                    {BAR_HEIGHTS.map(([callH, putH], index) => (
                        <Box
                            key={index}
                            sx={{
                                flex: 1,
                                minWidth: 0,
                                display: 'flex',
                                alignItems: 'flex-end',
                                justifyContent: 'center',
                                gap: '2px',
                                height: '72%',
                            }}
                        >
                            <Box
                                sx={{
                                    width: '42%',
                                    height: `${callH}%`,
                                    bgcolor: colors.fill,
                                    borderRadius: '2px 2px 0 0',
                                }}
                            />
                            <Box
                                sx={{
                                    width: '42%',
                                    height: `${putH}%`,
                                    bgcolor: colors.muted,
                                    borderRadius: '2px 2px 0 0',
                                }}
                            />
                        </Box>
                    ))}
                </Box>
                <Box
                    sx={{
                        width: 52,
                        flexShrink: 0,
                        borderLeft: 1,
                        borderColor: colors.axis,
                        display: 'flex',
                        flexDirection: 'column',
                        justifyContent: 'space-between',
                        py: 1,
                        px: 0.5,
                    }}
                >
                    {[0, 1, 2, 3, 4].map((i) => (
                        <Box key={i} sx={{ width: '70%', height: 8, bgcolor: colors.scale, borderRadius: 0.5, ml: 'auto' }} />
                    ))}
                </Box>
            </Box>
            <Box
                sx={{
                    height: 28,
                    flexShrink: 0,
                    borderTop: 1,
                    borderColor: colors.axis,
                    display: 'flex',
                    alignItems: 'center',
                    px: 1,
                    gap: 2,
                }}
            >
                {[32, 40, 36, 44, 28].map((w, i) => (
                    <Box key={i} sx={{ width: w, height: 8, bgcolor: colors.scale, borderRadius: 0.5 }} />
                ))}
            </Box>
        </Box>
    )
}

export default memo(OiChartSkeletonPlaceholder)
